/**
 * @file  customer/index.ts
 * @description  customer service to manage all the customer related operations
 *
 * @author dlima (dlima-electron)
 *
 */
import * as _datetime from '../datetime';
import * as _cache from '../../services/cache';
import * as CustomerModels from '../../functions/customer/customer.model';
import * as _vehicleServices from '../Vehicle';
import { saveCustomer } from '../../functions/customer';
import { ContractRequest } from '../../functions/contract/contract.model';

/**
 * gets the customer information from the cache
 */
export const getCustomer = async (): Promise<CustomerModels.Customer> => {
  const customer = _cache.getItem<CustomerModels.Customer>('contact');
  return customer;
};

/**
 * saves a customer payment flag to the server and cache
 */
export const cacheCustomer = async (customer: CustomerModels.Customer): Promise<void> => {
  _cache.setItem<CustomerModels.Customer>('contact', customer);
};

/**
 * saves a customer payment flag to the server and cache
 */
export const completePayment = async (): Promise<void> => {
  const customer = _cache.getItem<CustomerModels.Customer>('contact');
  customer.payment = true;
  const result: CustomerModels.Customer = await saveCustomer(customer);
  _cache.setItem<CustomerModels.Customer>('contact', {
    ...customer,
    ...result
  });
};

/**
 * saves a customer contract flag to the server and cache
 */
export const completeContract = async (): Promise<void> => {
  const customer = _cache.getItem<CustomerModels.Customer>('contact');
  customer.contract = true;
  const result: CustomerModels.Customer = await saveCustomer(customer);
  _cache.setItem<CustomerModels.Customer>('contact', {
    ...customer,
    ...result
  });
};

/**
 * clear vehicle informations from customer
 */
export const clearVehicleInformations = async (): Promise<void> => {
  const currentCustomer = _cache.getItem<CustomerModels.Customer>('contact');
  _cache.setItem('contact', {
    ...currentCustomer,
    vin: '',
    mileage: ''
  });
};

/**
 * Gets empty customer into with cached vehicle.
 * @returns Vehicle data.
 */
export const getEmptyCustomer = (): CustomerModels.Customer => {
  const cachedCar = _vehicleServices.getVehicle();
  const emptyContact = {
    id: '',
    created: _datetime.now() as Date,
    updated: _datetime.now() as Date,
    ip: '',
    firstName: '',
    midName: '',
    lastName: '',
    email: '',
    phone: '',
    homeAddress: {
      address: '',
      address2: '',
      city: '',
      state: '',
      zip: ''
    },
    billingAdress: {
      address: '',
      address2: '',
      city: '',
      state: '',
      zip: ''
    },
    cars: [],
    payment: false,
    contract: false,
    product: {
      dealerCost: 0,
      termMiles: '',
      termMonths: '',
      saleDate: _datetime.now() as Date,
      taxRate: 0
    },
    vin: cachedCar?.vin ?? '',
    mileage: ''
  };

  return emptyContact;
};

/**
 * Returns whether customer is renewal or not.
 */
export const isRenewal = async (): Promise<boolean> => {
  const customer = await getCustomer();
  return customer?.renewal === true;
};

/*
 * use to check if the customer has done a renewal contract which can be
 * used to prevent the user from visiting other pages
 */
export const hasRenewedContract = async (): Promise<boolean> => {
  const customer = await getCustomer();
  return customer?.renewal === true && customer?.contract;
};

/**
 * Checks if the current customer has contracted
 * @returns true if the customer has contracted
 */
export const hasContracted = async (): Promise<boolean> => {
  const customer = await getCustomer();
  return customer?.contract || false;
};

/**
 * Compare customer form records
 * @param obj1
 * @param obj2
 * @returns flag that indicates if the objects are different
 */
export const equalsCustomer = (obj1: CustomerModels.Customer, obj2: CustomerModels.Customer) : boolean => {
  return (obj1.firstName === obj2.firstName &&
      obj1.lastName === obj2.lastName &&
      obj1.email === obj2.email &&
      obj1.phone === obj2.phone &&
      obj1.homeAddress?.address === obj2.homeAddress?.address &&
      obj1.homeAddress?.address2 === obj2.homeAddress?.address2 &&
      obj1.homeAddress?.city === obj2.homeAddress?.city &&
      obj1.homeAddress?.state === obj2.homeAddress?.state &&
      obj1.homeAddress?.zip === obj2.homeAddress?.zip);
};

/**
 * Verify if customer form has any change after draft submission
 * @returns a flag that incated if customer form was changed
 */
export const customerHasChange = async () : Promise<boolean> => {
  const currentCustomer = await getCustomer();
  const currentContractRequest = _cache.getItem<ContractRequest>(_cache.Key.contractRequest);
  return !equalsCustomer(currentCustomer, currentContractRequest.customer);
};
